import React, { CSSProperties, FunctionComponent, useCallback, useEffect, useState } from 'react';

import Flex from '@gojob/ui-bits/Flex';
import { Option } from '@gojob/ui-bits/PlacesAutocomplete/useRemoteOptions';
import Loadable from '@loadable/component';
import { useNavigate } from '@reach/router';
import { withPrefix } from 'gatsby';

import { DEFAULT_RADIUS } from 'src/components/InfiniteJobList/useQueryJobsCount';
import Count from 'src/components/SearchForm/IndicatorPanel/Count';
import FilterButton from 'src/components/SearchForm/IndicatorPanel/FilterButton';
import searchTracking from 'src/tracking/search';
import createPath from 'src/utils/create-path';

export type IndicatorPanelProps = {
  keyword?: string;
  city?: Option | null;
  radius?: number;
  loading?: boolean;
  count?: number;
  className?: string;
  style?: CSSProperties;
};

const Filters = Loadable(() => import('src/components/SearchForm/IndicatorPanel/Filters'));

const IndicatorPanel: FunctionComponent<IndicatorPanelProps> = (props) => {
  const { keyword, city, radius = DEFAULT_RADIUS, loading, count, className, style } = props;

  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen((current) => !current), []);

  useEffect(() => {
    const [lng, lat] = city?.centre?.coordinates || [];
    if (!lng || !lat) {
      return setOpen(false);
    }
  }, [city]);

  const navigate = useNavigate();
  const handleApplyFilters = useCallback(
    (radius?: number) => {
      const path = createPath({ keyword, city, radius, type: 'search' });
      navigate(withPrefix(path));
      /** Track this event */
      searchTracking.submitJobSearch();
      /** Close filters panel / dialog */
      setOpen(false);
    },
    [navigate, keyword, city, radius, setOpen]
  );

  return (
    <>
      <Flex
        margin={[2, -1]}
        justifyContent="space-between"
        alignItems="center"
        className={className}
        style={style}
      >
        <Flex flex="1 1 auto" margin={[0, 1]}>
          <Count city={city} count={count} loading={loading} />
        </Flex>

        <Flex flex="0 0 auto" margin={[0, 1]}>
          <FilterButton city={city} radius={radius} onClick={toggleOpen} />
        </Flex>
      </Flex>

      {/** Lazy-loaded Filters panel (or Dialog) goes here */}
      <Filters
        open={open}
        keyword={keyword}
        city={city}
        radius={radius}
        onApply={handleApplyFilters}
        onCancel={toggleOpen}
      />
    </>
  );
};

export default IndicatorPanel;
